import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { DeltaItem } from '../models/delta-item.model';

@Component({
  selector: 'biz-view-delta-list',
  templateUrl: './view-delta-list.component.html',
  styleUrls: ['./view-delta-list.component.scss']
})
export class ViewDeltaListComponent implements OnInit, OnChanges {
  // Data provided by parent
  @Input() items: DeltaItem[] = [];
  @Input() filterName: string = '';
  @Input() filterDeltaType: string[] | null = null;
  @Input() filterObjType: string | null = null;
  @Output() selectionChange = new EventEmitter<number>();
  @Output() selectedIdsChange = new EventEmitter<string[]>();
  @Output() viewChangesClick = new EventEmitter<DeltaItem>();
  deltaItems: DeltaItem[] = [];
  filteredItems: DeltaItem[] = [];
  selectedIds: string[] = [];
  // Paginator state
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;
  paginatedItems: DeltaItem[] = [];
  availableDeltaTypes: string[] = [];
  availableObjTypes: string[] = [];

  constructor() {}

  ngOnInit() {
    // Initialize local data from inputs
    this.deltaItems = Array.isArray(this.items) ? this.items.slice() : [];
    // Keep only selections that still exist
    this.selectedIds = this.selectedIds.filter(id => this.deltaItems.some(item => item.id === id));
    this.applyFilter();
    this.updateAvailableOptions();
    this.updatePage();
    this.emitSelectionCount();
    this.emitSelectedIds();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']) {
      this.deltaItems = Array.isArray(this.items) ? this.items.slice() : [];
      // Reset pagination when data changes
      this.pageIndex = 0;
      // Remove selected ids that no longer exist
      this.selectedIds = this.selectedIds.filter(id => this.deltaItems.some(item => item.id === id));
      this.updateAvailableOptions();
      this.applyFilter();
      this.updatePage();
      this.emitSelectionCount();
      this.emitSelectedIds();
    }
    if (changes['filterName'] || changes['filterDeltaType'] || changes['filterObjType']) {
      this.pageIndex = 0;
      this.applyFilter();
      this.updatePage();
    }
  }

  viewChanges(item: DeltaItem) {
    this.viewChangesClick.emit(item);
  }

  trackById(index: number, item: DeltaItem) {
    return item.id;
  }

  isSelected(item: DeltaItem) {
    return this.selectedIds.includes(item.id);
  }

  toggleItem(item: DeltaItem) {
    const idx = this.selectedIds.indexOf(item.id);
    if (idx >= 0) {
      this.selectedIds.splice(idx, 1);
    } else {
      this.selectedIds.push(item.id);
    }
    this.emitSelectionCount();
    this.emitSelectedIds();
  }

  isAllSelected() {
    return this.filteredItems.length > 0 && this.selectedIds.length === this.filteredItems.length;
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.selectedIds = [];
    } else {
      this.selectedIds = this.filteredItems.map(item => item.id);
    }
    this.emitSelectionCount();
    this.emitSelectedIds();
  }

  onPaginationEvent(event: any) {
    // event: { pageIndex: number, pageSize: number }
    if (event) {
      if (typeof event.pageIndex === 'number') {
        this.pageIndex = event.pageIndex;
      }
      if (typeof event.pageSize === 'number') {
        this.pageSize = event.pageSize;
      }
      this.updatePage();
    }
  }

  private updatePage() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedItems = this.filteredItems.slice(start, end);
  }

  private applyFilter() {
    const text = (this.filterName || '').trim().toLowerCase();
    if (text) {
      this.filteredItems = this.deltaItems.filter(item => {
        const name = ((item.displayName || item.name || '') + '').toLowerCase();
        const ancestor = ((item.ancestor || '') + '').toLowerCase();
        return name.includes(text) || ancestor.includes(text);
      });
    } else {
      this.filteredItems = this.deltaItems.slice();
    }
    // Apply select filters
    if (this.filterDeltaType && this.filterDeltaType.length > 0) {
      const selectedTypes = this.filterDeltaType.map(dt => dt.toLowerCase());
      this.filteredItems = this.filteredItems.filter(i => 
        selectedTypes.includes(((i.deltaType || '') + '').toLowerCase())
      );
    }
    if (this.filterObjType) {
      const sel = (this.filterObjType + '').toLowerCase();
      this.filteredItems = this.filteredItems.filter(i => ((i.objType || '') + '').toLowerCase() === sel);
    }
    this.totalItems = this.filteredItems.length;
  }

  private updateAvailableOptions() {
    const unique = <T extends string>(arr: T[]) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b));
    // Distinct values based on the full table data; unaffected by active filters
    this.availableDeltaTypes = unique(this.deltaItems.map(i => (i.deltaType || '') as string).filter(v => !!v));
    this.availableObjTypes = unique(this.deltaItems.map(i => (i.objType || '') as string).filter(v => !!v));
  }

  // Filters are provided via @Input() and handled in ngOnChanges

  private emitSelectionCount() {
    this.selectionChange.emit(this.selectedIds.length);
  }

  private emitSelectedIds() {
    this.selectedIdsChange.emit(this.selectedIds.slice());
  }
}
